import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { research, parsePositions, COUNTRIES, type PositionList, type RunSummary } from "../lib/research";
import ResearchPaste from "./ResearchPaste";

// Rough per-group spend (Claude Opus 5 + ~10 web searches), measured at ~$0.54 a group.
// Shown before running; the real cost is recorded per group and shown on the run.
const EST = { fast: { low: 0.2, high: 0.6 }, thorough: { low: 0.4, high: 1.2 } };

export default function Research() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "paste" ? "paste" : "ai";
  const [lists, setLists] = useState<PositionList[]>([]);
  const [listId, setListId] = useState<number | null>(Number(params.get("list")) || null);
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("");
  const [domain, setDomain] = useState("");
  const [mode, setMode] = useState<"fast" | "thorough">("fast");
  const EST_PER_GROUP = EST[mode];
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    research.lists().then(d => {
      setLists(d.lists);
      setListId(prev => (prev && d.lists.some(l => l.id === prev)) ? prev : d.lists[0]?.id ?? null);
    }).catch(e => setError(String(e)));
    research.runs().then(d => setRuns(d.runs)).catch(e => setError(String(e)));
  }, []);

  const list = lists.find(l => l.id === listId) || null;
  const allGroups = useMemo(() => parsePositions(list?.content || ""), [list]);
  // Roles removed for this run only — the saved list keeps them. Each removed role
  // (and each emptied group, which is one paid search) comes off the cost.
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  useEffect(() => { setRemoved(new Set()); }, [listId]);
  const key = (gi: number, role: string) => `${gi}|${role}`;
  const groups = useMemo(
    () => allGroups
      .map((g, gi) => ({ ...g, roles: g.roles.filter(r => !removed.has(key(gi, r))) }))
      .filter(g => g.roles.length),
    [allGroups, removed],
  );
  const roleCount = groups.reduce((n, g) => n + g.roles.length, 0);
  const totalRoles = allGroups.reduce((n, g) => n + g.roles.length, 0);

  function setRoles(keys: string[], remove: boolean) {
    setRemoved(prev => {
      const n = new Set(prev);
      keys.forEach(k => (remove ? n.add(k) : n.delete(k)));
      return n;
    });
  }
  const allKeys = () => allGroups.flatMap((g, gi) => g.roles.map(r => key(gi, r)));

  async function start() {
    setError(null);
    if (!list) { setError("Choose a role list."); return; }
    if (!groups.length) { setError("All roles are removed — add at least one back."); return; }
    if (!company.trim()) { setError("Enter the company name."); return; }
    const lo = (groups.length * EST_PER_GROUP.low).toFixed(2);
    const hi = (groups.length * EST_PER_GROUP.high).toFixed(2);
    if (!confirm(
      `Find who holds ${roleCount} role(s) from "${list.name}" at ${company}${country ? ` (${country})` : ""}?\n\n` +
      `ESTIMATED COST: USD $${lo} – $${hi} (${groups.length} search group(s), Claude + web search).\n` +
      `${mode === "fast" ? "Fast" : "Thorough"} mode — up to 4 groups run at the same time. Keep the page open while it runs.`
    )) return;
    setBusy(true);
    try {
      const r = await research.createRun({ company: company.trim(), country: country.trim() || undefined, domain: domain.trim() || undefined, groups, mode });
      nav(`/outreach/research/${r.id}?autostart=1`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Research</h1>
        <p className="mt-1 text-sm text-slate-500">
          Find who holds your roles at a company: let the AI search the web, or paste results you already have (e.g. from ChatGPT) for free.
        </p>
      </div>

      <div className="flex rounded-lg bg-slate-100 p-0.5 w-fit">
        {([["ai", "AI search"], ["paste", "Paste results (free)"]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setParams(k === "paste" ? { tab: "paste" } : {}, { replace: true })}
                  className={`rounded-md px-4 py-1.5 text-sm font-medium ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "paste" ? <ResearchPaste /> : <>

      {error && <div className="card p-4 text-sm text-rose-700">{error}</div>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="card space-y-4 p-5">
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-slate-600">Role list</label>
              <Link to="/outreach/roles" className="text-[11px] text-indigo-600 hover:underline">Manage roles →</Link>
            </div>
            {lists.length === 0
              ? <div className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  No role lists yet. <Link className="underline" to="/outreach/roles">Create one in Roles</Link> first.
                </div>
              : <select className="w-full !text-sm !py-2" value={listId ?? ""} onChange={e => setListId(Number(e.target.value))}>
                  {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Country</label>
            <input className="w-full !text-sm !py-2 !px-3" list="research-countries" value={country} onChange={e => setCountry(e.target.value)} placeholder="Spain" />
            <datalist id="research-countries">{COUNTRIES.map(c => <option key={c} value={c} />)}</datalist>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Company</label>
            <input className="w-full !text-sm !py-2 !px-3" value={company} onChange={e => setCompany(e.target.value)} placeholder="Vodafone Spain" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Email domain <span className="font-normal text-slate-400">(optional)</span></label>
            <input className="w-full !text-sm !py-2 !px-3" value={domain} onChange={e => setDomain(e.target.value)} placeholder="vodafone.com" />
            <p className="mt-1 text-[11px] text-slate-500">Leave blank to learn it from contacts you already hold for this company.</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Search depth</label>
            <div className="flex rounded-lg bg-slate-100 p-0.5">
              {(["fast", "thorough"] as const).map(m => (
                <button key={m} onClick={() => setMode(m)}
                        className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${mode === m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                  {m === "fast" ? "Fast" : "Thorough"}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              {mode === "fast"
                ? "About a minute per group, fewer searches. Good for senior, well-known roles."
                : "Several minutes per group, more searches and page reads. For hard-to-find roles."}
            </p>
          </div>
          <button className="btn-primary w-full" disabled={busy || !groups.length || !company.trim()} onClick={start}>
            {busy ? "Starting…" : "Run research"}
          </button>
          {groups.length > 0 && (
            <p className="text-center text-[11px] text-slate-500">
              {groups.length} search group(s) · est. USD ${(groups.length * EST_PER_GROUP.low).toFixed(2)}–${(groups.length * EST_PER_GROUP.high).toFixed(2)}
            </p>
          )}
        </div>

        <div className="card p-5 lg:col-span-2">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-slate-900">
              {list ? `Roles in “${list.name}”` : "Roles"}
              {totalRoles > 0 && <span className="ml-2 font-normal text-slate-500">{roleCount} of {totalRoles} selected · {groups.length} search{groups.length === 1 ? "" : "es"}</span>}
            </div>
            {totalRoles > 0 && (
              <div className="flex gap-1.5">
                <button className="btn-secondary !text-xs !py-1 !px-2" disabled={removed.size === 0} onClick={() => setRemoved(new Set())}>Add all</button>
                <button className="btn-secondary !text-xs !py-1 !px-2" disabled={roleCount === 0} onClick={() => setRoles(allKeys(), true)}>Remove all</button>
              </div>
            )}
          </div>
          {allGroups.length === 0
            ? <p className="text-sm text-slate-500">Choose a role list to see what will be searched.</p>
            : (
              <>
                <p className="mb-2 text-[11px] text-slate-500">Click × to leave a role out of this search. Your saved list is not changed.</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {allGroups.map((g, gi) => {
                    const keys = g.roles.map(r => key(gi, r));
                    const kept = keys.filter(k => !removed.has(k)).length;
                    return (
                      <div key={`${gi}-${g.name}`} className={`rounded-md border p-3 ${kept ? "border-slate-200" : "border-dashed border-slate-200 bg-slate-50"}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className={`text-xs font-semibold ${kept ? "text-slate-800" : "text-slate-400"}`}>
                            {g.name} <span className="font-normal text-slate-400">({kept}/{g.roles.length})</span>
                          </div>
                          <button className="text-[11px] text-indigo-600 hover:underline" onClick={() => setRoles(keys, kept > 0)}>
                            {kept > 0 ? "remove group" : "add group"}
                          </button>
                        </div>
                        <ul className="mt-1.5 space-y-0.5">
                          {g.roles.map(r => {
                            const off = removed.has(key(gi, r));
                            return (
                              <li key={r} className="flex items-center justify-between gap-2 text-xs">
                                <span className={off ? "text-slate-400 line-through" : "text-slate-700"}>{r}</span>
                                {off
                                  ? <button className="shrink-0 text-[11px] text-indigo-600 hover:underline" onClick={() => setRoles([key(gi, r)], false)}>add</button>
                                  : <button className="shrink-0 px-1 text-sm leading-none text-slate-400 hover:text-rose-600" title="Remove from this search" onClick={() => setRoles([key(gi, r)], true)}>×</button>}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
        </div>
      </div>

      </>}

      <div className="card overflow-hidden">
        <div className="border-b border-slate-100 p-4 text-sm font-semibold">Past research</div>
        {runs.length === 0
          ? <div className="p-6 text-center text-sm text-slate-500">No research yet.</div>
          : (
            <div className="overflow-x-auto">
              <table className="refined">
                <thead><tr><th>Company</th><th>Country</th><th>Status</th><th>People</th><th>Added</th><th>Cost</th><th>By</th><th>When</th></tr></thead>
                <tbody>
                  {runs.map(r => (
                    <tr key={r.id}>
                      <td><Link className="font-medium text-slate-900 hover:underline" to={`/outreach/research/${r.id}`}>{r.company}</Link></td>
                      <td>{r.country}</td>
                      <td><span className={`pill ${r.status === "done" ? "pill-green" : r.status === "partial" ? "pill-amber" : "pill-slate"}`}>{r.status}</span></td>
                      <td>{r.people}</td>
                      <td>{r.added}</td>
                      <td className="font-mono text-xs">${Number(r.cost_usd || 0).toFixed(2)}</td>
                      <td className="text-xs text-slate-500">{r.created_by_email}</td>
                      <td className="text-xs text-slate-500">{new Date(r.created_at.replace(" ", "T") + "Z").toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}
